/**
 * useAuditLog Hook
 * Provides convenient methods for creating audit log entries
 */

import { useCallback } from 'react';
import { SessionAuditClient, AuditAction } from '../services';

interface UseAuditLogOptions {
  batchId: string | null;
  sessionId: string | null;
  sessionClient: SessionAuditClient;
}

interface UseAuditLogReturn {
  logSave: (fieldsChanged?: string[]) => Promise<void>;
  logSubmission: (assignedQP: string) => Promise<void>;
  logApproval: (comments?: string) => Promise<void>;
  logRejection: (reason: string) => Promise<void>;
  logCustomAction: (action: number, details?: string) => Promise<void>;
}

/**
 * Hook for logging audit trail entries
 *
 * @example
 * const { logSave, logSubmission } = useAuditLog({
 *   batchId: 'B-2026-0147',
 *   sessionId: '12345',
 *   sessionClient: sessionApi
 * });
 *
 * // Log a save action
 * await logSave(['productName', 'batchNumber']);
 *
 * // Log submission
 * await logSubmission('qp@company.com');
 */
export const useAuditLog = ({
  batchId,
  sessionId,
  sessionClient
}: UseAuditLogOptions): UseAuditLogReturn => {
  const logSave = useCallback(async (fieldsChanged?: string[]) => {
    if (!batchId || !sessionId) {
      console.warn('Cannot log save: missing batchId or sessionId');
      return;
    }

    try {
      await sessionClient.logSave(
        batchId,
        sessionId,
        undefined, // page - not available in this context
        fieldsChanged
      );
    } catch (err) {
      console.error('Failed to log save:', err);
      // Don't throw - audit logging failures shouldn't block user operations
    }
  }, [batchId, sessionId, sessionClient]);

  const logSubmission = useCallback(async (assignedQP: string) => {
    if (!batchId || !sessionId) {
      console.warn('Cannot log submission: missing batchId or sessionId');
      return;
    }

    try {
      await sessionClient.logSubmission(
        batchId,
        sessionId,
        assignedQP
      );
    } catch (err) {
      console.error('Failed to log submission:', err);
      throw err; // Submission logging is critical - throw error
    }
  }, [batchId, sessionId, sessionClient]);

  const logApproval = useCallback(async (comments?: string) => {
    if (!batchId || !sessionId) {
      console.warn('Cannot log approval: missing batchId or sessionId');
      return;
    }

    try {
      await sessionClient.logApproval(
        batchId,
        sessionId,
        comments
      );
    } catch (err) {
      console.error('Failed to log approval:', err);
      throw err; // Approval logging is critical - throw error
    }
  }, [batchId, sessionId, sessionClient]);

  const logRejection = useCallback(async (reason: string) => {
    if (!batchId || !sessionId) {
      console.warn('Cannot log rejection: missing batchId or sessionId');
      return;
    }

    try {
      await sessionClient.logRejection(
        batchId,
        sessionId,
        reason
      );
    } catch (err) {
      console.error('Failed to log rejection:', err);
      throw err; // Rejection logging is critical - throw error
    }
  }, [batchId, sessionId, sessionClient]);

  const logCustomAction = useCallback(async (action: number, details?: string) => {
    if (!batchId || !sessionId) {
      console.warn('Cannot log custom action: missing batchId or sessionId');
      return;
    }

    try {
      await sessionClient.createAuditLog({
        batchId,
        sessionId,
        action,
        details
      });
    } catch (err) {
      console.error('Failed to log custom action:', err);
      // Don't throw - custom logging failures shouldn't block operations
    }
  }, [batchId, sessionId, sessionClient]);

  return {
    logSave,
    logSubmission,
    logApproval,
    logRejection,
    logCustomAction
  };
};
